/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Share a report, dashboard or folder with users, teams or roles.
 *
 * One dialog for all three resource types, because the backend ACL is one
 * polymorphic table and the question is identical in each case. Sharing with a
 * team reaches its sub-teams, which the dialog says out loud — otherwise
 * somebody shares with "Sales" and then shares again with every sub-team
 * because they cannot tell whether the first one worked.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Autocomplete, Box, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, MenuItem, TextField, Tooltip,
  Typography,
} from '@mui/material';
import {
  DeleteOutlineRounded, GroupsRounded, PersonRounded, ShieldOutlined,
} from '@mui/icons-material';

import MDButton from 'components/MDButton';
import { useAuth } from 'auth/Auth';
import colors from 'styles/tokens/colors';

const ACCESS_LEVELS = [
  { value: 'view', label: 'Can view', hint: 'Run it and export it.' },
  { value: 'edit', label: 'Can edit', hint: 'Change the report itself.' },
  { value: 'manage', label: 'Can manage', hint: 'Edit, share and delete it.' },
];

const PRINCIPAL_ICON = {
  user: PersonRounded,
  team: GroupsRounded,
  role: ShieldOutlined,
};

export default function ShareDialog({ open, onClose, resourceType, resource }) {
  const { axiosInstance } = useAuth();

  const [shares, setShares] = useState([]);
  const [candidates, setCandidates] = useState({ user: [], team: [], role: [] });
  const [principalType, setPrincipalType] = useState('user');
  const [selected, setSelected] = useState(null);
  const [access, setAccess] = useState('view');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const resourceId = resource?.id;

  const loadShares = useCallback(async () => {
    if (!resourceId) return;
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/analytics/shares', {
        params: { resource_type: resourceType, resource_id: resourceId },
      });
      setShares(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(readError(err, 'The sharing list could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, resourceType, resourceId]);

  useEffect(() => { if (open) loadShares(); }, [open, loadShares]);

  // Users, teams and roles come from three different endpoints, and teams and
  // roles only exist on tenants past migration 030. A tenant without them
  // shares with individuals, which is a smaller feature rather than an error.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const [users, teams, roles] = await Promise.all([
        axiosInstance.get('/users/').then((r) => r.data).catch(() => []),
        axiosInstance.get('/teams').then((r) => r.data).catch(() => []),
        axiosInstance.get('/roles').then((r) => r.data).catch(() => []),
      ]);
      if (cancelled) return;
      setCandidates({
        user: (Array.isArray(users) ? users : []).map((u) => ({
          id: u.id,
          label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email,
          sub: u.email,
        })),
        team: (Array.isArray(teams) ? teams : []).map((t) => ({
          id: t.id, label: t.name, sub: t.description || '',
        })),
        role: (Array.isArray(roles) ? roles : []).map((r) => ({
          id: r.id, label: r.name, sub: r.description || '',
        })),
      });
    })();
    return () => { cancelled = true; };
  }, [open, axiosInstance]);

  const alreadyShared = useMemo(
    () => new Set(shares.map((s) => `${s.principal_type}:${s.principal_id}`)),
    [shares],
  );

  const options = useMemo(
    () => (candidates[principalType] ?? [])
      .filter((c) => !alreadyShared.has(`${principalType}:${c.id}`)),
    [candidates, principalType, alreadyShared],
  );

  const addShare = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axiosInstance.post('/analytics/shares', {
        resource_type: resourceType,
        resource_id: resourceId,
        principal_type: principalType,
        principal_id: selected.id,
        access,
      });
      setSelected(null);
      setError(null);
      await loadShares();
    } catch (err) {
      setError(readError(err, 'That share could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  const removeShare = async (shareId) => {
    try {
      await axiosInstance.delete(`/analytics/shares/${shareId}`);
      await loadShares();
    } catch (err) {
      setError(readError(err, 'That share could not be removed.'));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>
        Share “{resource?.name}”
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          People you share with see the records they are already allowed to see,
          not yours.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box display="flex" gap={1} alignItems="flex-start" mb={1}>
          <TextField
            select size="small" label="With" value={principalType}
            onChange={(e) => { setPrincipalType(e.target.value); setSelected(null); }}
            sx={{ width: 110, flexShrink: 0 }}
          >
            <MenuItem value="user">Person</MenuItem>
            <MenuItem value="team">Team</MenuItem>
            <MenuItem value="role">Role</MenuItem>
          </TextField>

          <Autocomplete
            size="small"
            sx={{ flex: 1, minWidth: 0 }}
            options={options}
            value={selected}
            onChange={(_, value) => setSelected(value)}
            getOptionLabel={(option) => option?.label ?? ''}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box>
                  <Typography variant="body2">{option.label}</Typography>
                  {option.sub && (
                    <Typography variant="caption" color="text.secondary">
                      {option.sub}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label={`Choose a ${principalType}`} />
            )}
            noOptionsText={
              candidates[principalType]?.length
                ? 'Everyone here already has access'
                : `No ${principalType}s available`
            }
          />

          <TextField
            select size="small" label="Access" value={access}
            onChange={(e) => setAccess(e.target.value)}
            sx={{ width: 140, flexShrink: 0 }}
          >
            {ACCESS_LEVELS.map((level) => (
              <MenuItem key={level.value} value={level.value}>{level.label}</MenuItem>
            ))}
          </TextField>

          <MDButton
            variant="gradient" color="info" size="small"
            disabled={!selected || saving}
            onClick={addShare}
            sx={{ flexShrink: 0, mt: 0.25 }}
          >
            Add
          </MDButton>
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          {ACCESS_LEVELS.find((l) => l.value === access)?.hint}
          {principalType === 'team' && ' Sharing with a team also reaches its sub-teams.'}
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" py={3}><CircularProgress size={22} /></Box>
        ) : !shares.length ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Not shared with anyone yet. Only you can see it.
            </Typography>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={0.5}>
            {shares.map((share) => {
              const Icon = PRINCIPAL_ICON[share.principal_type] ?? PersonRounded;
              return (
                <Box
                  key={share.id}
                  display="flex" alignItems="center" gap={1.5}
                  sx={{
                    px: 1.5, py: 1, borderRadius: '10px',
                    '&:hover': { backgroundColor: colors.background.offWhite },
                  }}
                >
                  <Icon sx={{ fontSize: 18, color: colors.text.light }} />
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body2" noWrap>
                      {/* A share whose principal was deleted resolves to nobody
                          and is inert; saying so beats showing a bare id. */}
                      {share.principal_label
                        ?? `Deleted ${share.principal_type} (#${share.principal_id})`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {share.principal_type}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={ACCESS_LEVELS.find((l) => l.value === share.access)?.label
                           ?? share.access}
                    sx={{
                      backgroundColor: colors.chip.selectedBackground,
                      color: colors.chip.selectedText, fontWeight: 600,
                    }}
                  />
                  <Tooltip title="Remove">
                    <IconButton size="small" onClick={() => removeShare(share.id)}>
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <MDButton variant="text" color="dark" onClick={onClose}>Done</MDButton>
      </DialogActions>
    </Dialog>
  );
}

ShareDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  resourceType: PropTypes.oneOf(['report', 'dashboard', 'folder']).isRequired,
  resource: PropTypes.object,
};

function readError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return fallback;
}
